package com.arena_web.ui.localization

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys

@Composable
fun rememberLanguage(): LanguageState {
    val stored = Storage.get(StorageKeys.LANGUAGE)
    // Explicit user choice wins; first visit follows the browser language.
    val initialLang = if (stored != null) {
        Language.fromCode(stored)
    } else {
        Language.fromBrowserTag(kotlinx.browser.window.navigator.language) ?: Language.ENGLISH
    }
    var currentLanguage by remember { mutableStateOf(initialLang) }

    LaunchedEffect(currentLanguage) {
        Storage.save(StorageKeys.LANGUAGE, currentLanguage.code)
    }

    return remember(currentLanguage) {
        LanguageState(
            currentLanguage = currentLanguage,
            strings = currentLanguage.strings,
            onChange = { newLang ->
                currentLanguage = newLang
            }
        )
    }
}

data class LanguageState(
    val currentLanguage: Language,
    val strings: AppStrings,
    val onChange: (Language) -> Unit
)

/**
 * Current strings for non-composable contexts (view models, pick helpers).
 * Mirrors [rememberLanguage]'s resolution: explicit user choice from Storage
 * wins; first visit follows the browser language.
 */
fun currentAppStrings(): AppStrings {
    val stored = Storage.get(StorageKeys.LANGUAGE)
    return if (stored != null) {
        Language.fromCode(stored).strings
    } else {
        (Language.fromBrowserTag(kotlinx.browser.window.navigator.language) ?: Language.ENGLISH).strings
    }
}

val LocalLanguageState = compositionLocalOf<LanguageState> {
    error("LanguageState not provided")
}

@Composable
fun ProvideLanguage(
    languageState: LanguageState = rememberLanguage(),
    content: @Composable () -> Unit
) {

    CompositionLocalProvider(
        LocalLanguageState provides languageState,
        LocalAppStrings provides languageState.strings
    ) {
        content()
    }
}
